import {
  ElementController,
  FORM_EVENT,
  PAGE_UPDATER_EVENT,
  afterNextPaint,
  hasNodeName,
  isObject,
  isUndefined,
  selectSingle,
  getData,
} from 'js-common';

import { BASKET_CONTROLLER_ID } from './constants';
import { showFloatingFeedback } from './utils';

const BASKET_COUNT_ANIMATION_CLASS = 'basket-icon__count--new';

/**
 * Controller for the shopping basket popout.
 */
export default class Basket extends ElementController {
  static id = BASKET_CONTROLLER_ID;

  /**
   * DOM ready.
   */
  init() {
    this.basket = this.selectSingle('basket');
    this.basketToggle = this.selectSingle('basket-toggle');
    this.basketCount = this.selectSingle('basket-count');
    this.basketCountNumber = this.selectSingle('basket-count-number');

    this.on(document.body, FORM_EVENT.response, this.handleFormResponse);
    this.on(document.body, PAGE_UPDATER_EVENT.update, this.handlePageUpdate);
  }

  /**
   * Check if the basket is currently open.
   *
   * @returns {boolean}
   */
  get isBasketOpen() {
    return this.basketToggle.getAttribute('aria-expanded') === 'true';
  }

  /**
   * Update the basket product number with an animation.
   *
   * @param {number} count - The number of items in the basket.
   */
  setBasketCount = (count) => {
    if (count) {
      const current = Number(this.basketCountNumber.textContent);
      // No animation if the number is the same
      if (current === count) {
        return;
      }

      this.basketCount.removeAttribute('hidden');
      this.basketCount.classList.remove(BASKET_COUNT_ANIMATION_CLASS);
      this.basketCountNumber.textContent = count;
      // Wait for paint before adding the class back, otherwise CSS animations
      // may not trigger.
      afterNextPaint(() => {
        this.basketCount.classList.add(BASKET_COUNT_ANIMATION_CLASS);

        // Open the basket when adding products.
        if (!this.isBasketOpen && current < count) {
          this.basketToggle.click();
        }
      });
    } else {
      this.basketCount.setAttribute('hidden', '');
    }
  };

  /**
   * Update the basket if there is data for it in a form response.
   *
   * @param {Event} e - Custom event.
   * @param {object} data - Extra response data.
   */
  handleFormResponse = (e, data) => {
    if (isObject(data)) {
      if (data.basket) {
        this.basket.innerHTML = data.basket;
      }
      if (!isUndefined(data.productCount)) {
        this.setBasketCount(data.productCount);
      }
    }
  };

  /**
   * Show an 'added' text when an add-to-cart form is updated.
   *
   * @param {Event} e - Custom event.
   * @param {object} type - Page update type.
   */
  handlePageUpdate = (e, type) => {
    if (
      type === 'replace' &&
      e.target &&
      hasNodeName(e.target, 'add-to-cart-form')
    ) {
      const submit = selectSingle(e.target, 'button');
      const text = getData(e.target, 'added-text');
      if (submit && text) {
        afterNextPaint(() => {
          showFloatingFeedback(submit, text);
        });
      }
    }
  };
}
